<script setup lang="ts">
/**
 * The agent asked something (protocol `ask_user`). The question itself is shown as a normal agent reply by
 * AgentSurface; this is the form under it. One question: numbered options, a "Something else" line that takes
 * free text, Skip, and a submit arrow (up/down to move, 1-9 to pick, Enter to submit, Esc to skip). Several
 * (`questions`): asked one at a time with the same rows, checkboxes where more than one applies, and a typed
 * answer where a question has no options. Submitting or skipping a question moves on to the next; after the last,
 * the answers go back as one message, a line per question (a skipped one says so).
 *
 * Phones (`chatBubbles`, or a phone outside a transcript such as the call screen):
 * - one single-choice question with a few options: quick-reply chips under the question. A tap sends it at once
 *   and the chip flies into place as the creator's bubble; "Something else…" focuses the composer; Skip is quiet.
 * - several questions, multi-select or a long list: a summary card ("3 quick questions · Answer") that opens the
 *   QuestionSheet, one question per page.
 * No key hints and no autofocus on touch.
 */
import { computed, inject, nextTick, onMounted, ref, watch } from 'vue'
import type { UiQuestion } from '../reducer'
import type { AgentSession } from '../useAgentSession'
import { SKIP_TEXT, useAskForm } from '../askForm'
import { questionMode, questionSummary } from '../cards'
import { useCardMode } from '../cardMode'
import { requestComposerFocus } from '../composerFocus'
import { flyReplyToBubble } from '../replyMorph'
import { haptic } from '@/native/haptics'
import { vPress } from '@/directives/press'
import QuestionSheet from './QuestionSheet.vue'

const props = defineProps<{ question: UiQuestion; disabled?: boolean }>()
const emit = defineEmits<{ answer: [text: string] }>()

const { phone, inTranscript } = useCardMode()
const session = inject<AgentSession | null>('agentSession', null)

const form = useAskForm(() => props.question)
const { labelled, fields, picks, custom, skipped, otherIndex, isOn, valuesFor, answer } = form

const root = ref<HTMLElement | null>(null)
const activeField = ref(0)
/** The question being asked (several-question form only). */
const step = ref(0)
const cur = computed(() => fields.value[step.value])
const isLast = computed(() => step.value >= fields.value.length - 1)
watch(() => props.question.id, () => {
    step.value = 0
    activeField.value = 0
    sent.value = false
    sheetOpen.value = false
})
const inputs = new Map<number, HTMLInputElement>()
const setInput = (fi: number) => (el: unknown) => { if (el) inputs.set(fi, el as HTMLInputElement); else inputs.delete(fi) }

/** The question on screen can go on once it's answered (or is optional). */
const canSubmit = computed(() => !props.disabled && (labelled.value
    ? cur.value.optional || valuesFor(step.value).length > 0
    : answer.value.length > 0))
const countText = computed(() => {
    if (labelled.value) return `Question ${step.value + 1} of ${fields.value.length}`
    return picks[0]?.length ? '1 selected' : 'Pick one'
})

function pick(fi: number, i: number) {
    activeField.value = fi
    form.pick(fi, i)
    if (i === otherIndex(fi)) void nextTick(() => inputs.get(fi)?.focus())
}
const onCustomInput = form.onCustomInput
/** Show the next question, or send everything after the last. */
function advance() {
    if (!isLast.value) {
        step.value++
        activeField.value = step.value
        void nextTick(() => root.value?.focus({ preventScroll: true }))
        return
    }
    emit('answer', form.message.value)
}
function back() {
    if (step.value > 0 && !props.disabled) {
        step.value--
        activeField.value = step.value
        void nextTick(() => root.value?.focus({ preventScroll: true }))
    }
}
function submit() {
    if (!canSubmit.value) return
    if (labelled.value) skipped[step.value] = false
    if (labelled.value) advance()
    else emit('answer', answer.value)
}
function skip() {
    if (props.disabled) return
    if (!labelled.value) { emit('answer', SKIP_TEXT); return }
    form.skipField(step.value)
    advance()
}
/** One question: arrows change the pick. Several: arrows move between rows, across questions. */
function move(delta: number) {
    if (labelled.value) {
        const rows = [...(root.value?.querySelectorAll<HTMLElement>('[data-nav]') ?? [])]
        const at = rows.findIndex((r) => r === document.activeElement || r.contains(document.activeElement))
        rows[at < 0 ? (delta > 0 ? 0 : rows.length - 1) : (at + delta + rows.length) % rows.length]?.focus()
        return
    }
    const n = otherIndex(0) + 1
    const cur = picks[0][0]
    const next = cur === undefined ? (delta > 0 ? 0 : n - 1) : (cur + delta + n) % n
    pick(0, next)
    if (next !== otherIndex(0)) root.value?.focus()
}
function onKey(e: KeyboardEvent) {
    const typing = e.target instanceof HTMLInputElement
    if (e.key === 'ArrowDown') { e.preventDefault(); move(1) }
    else if (e.key === 'ArrowUp') { e.preventDefault(); move(-1) }
    else if (e.key === 'Enter' && !e.shiftKey && !e.isComposing) { e.preventDefault(); submit() }
    else if (e.key === 'Escape') { e.preventDefault(); skip() }
    else if (!typing && /^[1-9]$/.test(e.key) && Number(e.key) <= fields.value[activeField.value].options.length) {
        e.preventDefault()
        pick(activeField.value, Number(e.key) - 1)
    }
}
function onFocusIn(e: FocusEvent) {
    const fi = (e.target as HTMLElement | null)?.closest?.('[data-field]')?.getAttribute('data-field')
    if (fi != null) activeField.value = Number(fi)
}
/** Stagger index for the entrance animation, counted across all the rows. */
function rowIndex(_fi: number, i: number): number {
    return i
}

// Take focus so the keys work straight away, unless the creator is already typing somewhere (or on a phone,
// where focus would only bring up nothing useful and scroll the chat).
onMounted(() => {
    if (phone.value) return
    const a = document.activeElement
    if (!a || a === document.body || !(a instanceof HTMLTextAreaElement || a instanceof HTMLInputElement)) root.value?.focus({ preventScroll: true })
})

// ── phones ───────────────────────────────────────────────────────────────────────────────────────────────────
const mode = computed(() => (phone.value ? questionMode(props.question) : 'form'))
/** The sheet sits under the call screen, so outside a conversation several questions keep the in-place form. */
const phoneMode = computed(() => (mode.value === 'sheet' && !inTranscript ? 'form' : mode.value))
const chips = computed(() => fields.value[0]?.options ?? [])
/** A chip was sent: the chips fold away (and come back if the send fails). */
const sent = ref(false)
const picked = ref<number | null>(null)
const sheetOpen = ref(false)
const summary = computed(() => questionSummary(props.question))
const answeredText = computed(() => {
    const n = form.answeredCount.value
    return n ? `${n} of ${fields.value.length} answered` : ''
})

function sendChip(i: number, e: MouseEvent) {
    if (props.disabled || sent.value) return
    haptic('selection')
    picks[0] = [i]
    skipped[0] = false
    picked.value = i
    const text = labelled.value ? answer.value : chips.value[i]
    if (inTranscript) flyReplyToBubble(e.currentTarget as HTMLElement)
    sent.value = true
    emit('answer', text)
}
function somethingElse(e: MouseEvent) {
    if (props.disabled) return
    requestComposerFocus(e.currentTarget as Element)
}
function skipChips() {
    if (props.disabled || sent.value) return
    sent.value = true
    emit('answer', SKIP_TEXT)
}
function sendSheet(text: string) {
    sheetOpen.value = false
    emit('answer', text)
}
// The send didn't go through: bring the choices back.
watch(() => session?.error.value, (err) => {
    if (err && sent.value) {
        sent.value = false
        picked.value = null
    }
})
</script>

<template>
    <!-- Phones, one short question: quick replies, sent with one tap. -->
    <div v-if="phoneMode === 'chips'" class="qr" :class="{ 'is-standalone': !inTranscript }" data-testid="ask-user">
        <div class="agent-disclose qr-fold" :class="{ 'is-open': !sent }">
            <div>
                <TransitionGroup tag="div" name="agent-list" class="qr-list" role="group" :aria-label="question.question" appear>
                    <button
                        v-for="(o, i) in chips"
                        :key="o"
                        v-press.pill
                        type="button"
                        class="qr-chip"
                        :class="{ 'is-picked': picked === i }"
                        :style="{ '--i': i }"
                        :disabled="disabled || sent"
                        data-testid="quick-reply"
                        @click="sendChip(i, $event)"
                    >{{ o }}</button>
                    <div key="__more" class="qr-more" :style="{ '--i': chips.length }">
                        <button
                            v-if="inTranscript"
                            v-press.pill
                            type="button"
                            class="qr-chip is-other"
                            :disabled="disabled || sent"
                            data-testid="quick-reply-other"
                            @click="somethingElse"
                        >
                            <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M10.6 3.4l2 2-6.9 6.9-2.7.7.7-2.7 6.9-6.9z" /></svg>
                            Something else…
                        </button>
                        <button v-press.pill type="button" class="qr-skip" :disabled="disabled || sent" data-testid="quick-reply-skip" @click="skipChips">Skip</button>
                    </div>
                </TransitionGroup>
            </div>
        </div>
    </div>

    <!-- Phones, several questions (or many options): a card that opens the question sheet. -->
    <div v-else-if="phoneMode === 'sheet'" class="qs" data-testid="ask-user">
        <div
            v-press="{ variant: 'card', disabled: disabled }"
            class="qs-card"
            role="button"
            tabindex="0"
            aria-haspopup="dialog"
            :aria-label="`${summary}. Answer`"
            data-testid="question-card"
            @click="!disabled && (sheetOpen = true)"
            @keydown.enter.self.prevent="!disabled && (sheetOpen = true)"
        >
            <span class="qs-icon" aria-hidden="true"><svg viewBox="0 0 16 16"><path d="M6.1 6.2a2 2 0 113 1.7c-.7.4-1.1.9-1.1 1.6v.2M8 11.6v.1" /></svg></span>
            <span class="qs-body">
                <span class="qs-title">{{ summary }}</span>
                <span class="qs-sub">{{ answeredText || fields[0]?.question || question.question }}</span>
                <span v-if="fields.length > 1" class="qs-progress" aria-hidden="true">
                    <span v-for="(f, fi) in fields" :key="fi" :class="{ 'is-done': valuesFor(fi).length > 0 }" />
                </span>
            </span>
            <span class="qs-go">
                {{ form.answeredCount.value ? 'Continue' : 'Answer' }}
                <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M6 4l4 4-4 4" /></svg>
            </span>
        </div>
        <QuestionSheet
            :open="sheetOpen"
            :form="form"
            :question="question.question"
            :disabled="disabled"
            @close="sheetOpen = false"
            @send="sendSheet"
        />
    </div>

    <div v-else class="ask-wrap" data-testid="ask-user">
        <div
            ref="root"
            class="ask"
            :class="{ 'is-multi': labelled }"
            tabindex="0"
            :role="labelled ? 'group' : 'radiogroup'"
            aria-label="Choose an answer"
            @keydown="onKey"
            @focusin="onFocusIn"
        >
            <Transition name="ask-step" mode="out-in">
            <div
                :key="`${step}-${cur.question}`"
                class="field"
                :data-field="step"
                :role="labelled ? (cur.multi ? 'group' : 'radiogroup') : undefined"
                :aria-label="labelled ? cur.question : undefined"
                data-testid="ask-field"
            >
                <p v-if="labelled" class="field-label">
                    <span>{{ cur.question }}</span>
                    <span v-if="cur.optional" class="field-hint">Optional</span>
                    <span v-else-if="cur.multi" class="field-hint">Pick any</span>
                </p>
                <TransitionGroup tag="ul" name="agent-list" class="opts" appear>
                    <li v-for="(o, i) in cur.options" :key="o" :style="{ '--i': rowIndex(step, i) }">
                        <button
                            type="button"
                            class="opt"
                            :class="{ 'is-on': isOn(step, i), 'is-check': cur.multi }"
                            :role="cur.multi ? 'checkbox' : 'radio'"
                            :aria-checked="isOn(step, i)"
                            :disabled="disabled"
                            data-nav
                            @click="pick(step, i)"
                            @dblclick="!labelled && submit()"
                        >
                            <span class="tick" aria-hidden="true"><svg viewBox="0 0 16 16"><path d="M3.5 8.5l3 3 6-6.5" /></svg></span>
                            <span class="opt-text">{{ o }}</span>
                            <kbd v-if="i < 9" class="num" aria-hidden="true">{{ i + 1 }}</kbd>
                        </button>
                    </li>
                    <li :key="'__other'" :style="{ '--i': rowIndex(step, cur.options.length) }">
                        <label
                            class="opt is-other"
                            :class="{ 'is-on': cur.options.length > 0 && isOn(step, otherIndex(step)), 'is-check': cur.multi, 'is-text': !cur.options.length }"
                            @click="cur.options.length && pick(step, otherIndex(step))"
                        >
                            <span v-if="cur.options.length" class="tick" aria-hidden="true"><svg viewBox="0 0 16 16"><path d="M3.5 8.5l3 3 6-6.5" /></svg></span>
                            <input
                                :ref="setInput(step)"
                                v-model="custom[step]"
                                class="other-input"
                                type="text"
                                :placeholder="cur.options.length ? 'Something else' : 'Your answer'"
                                :aria-label="cur.options.length ? 'Something else' : cur.question"
                                :disabled="disabled"
                                data-nav
                                @focus="cur.options.length && !cur.multi && pick(step, otherIndex(step))"
                                @input="onCustomInput(step)"
                            >
                        </label>
                    </li>
                </TransitionGroup>
            </div>
            </Transition>

            <div class="foot">
                <span class="count">{{ countText }}</span>
                <button v-if="labelled && step > 0" type="button" class="skip" :disabled="disabled" @click="back">Back</button>
                <button type="button" class="skip" :disabled="disabled" @click="skip">Skip</button>
                <button
                    type="button"
                    class="go"
                    :class="{ 'is-ready': canSubmit }"
                    :disabled="!canSubmit"
                    :aria-label="labelled && !isLast ? 'Next question' : 'Send answer'"
                    @click="submit"
                >
                    <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M3 8h10M9 4l4 4-4 4" /></svg>
                </button>
            </div>
        </div>
        <p class="keys" aria-hidden="true">
            <span>↑↓ to navigate</span><span>Enter to submit</span><span>Esc to skip</span>
        </p>
    </div>
</template>

<style scoped>
.ask-wrap { min-width: 0; }
.ask {
    padding: 6px 6px 0;
    border-radius: 22px;
    background: var(--agent-card);
    border: 1px solid var(--agent-card-border);
    box-shadow: var(--agent-card-shadow);
    outline: none;
    transition: border-color 200ms ease, box-shadow 240ms ease;
}
.ask:focus-within { border-color: rgb(15 15 15 / 0.14); box-shadow: 0 0 0 4px rgb(15 15 15 / 0.03), var(--agent-card-shadow); }
html.dark .ask:focus-within { border-color: rgb(255 255 255 / 0.18); box-shadow: 0 0 0 4px rgb(255 255 255 / 0.04), var(--agent-card-shadow); }

.field + .field { margin-top: 6px; padding-top: 6px; border-top: 1px solid var(--agent-card-border); }
.ask.is-multi { padding-top: 2px; }
.field-label {
    display: flex; align-items: baseline; gap: 8px; padding: 10px 14px 6px;
    font-size: 13px; font-weight: 600; line-height: 1.35; color: var(--agent-text-1);
}
.field-hint { font-size: 11.5px; font-weight: 400; color: var(--agent-text-4); }
.opts { list-style: none; margin: 0; padding: 0; }
.opts li + li { position: relative; }
/* Hairline between rows, inset from the edges like the reference. */
.opts li + li::before {
    content: ''; position: absolute; top: 0; left: 14px; right: 14px; height: 1px; background: var(--agent-card-border);
}
.opt {
    position: relative; display: flex; align-items: center; gap: 12px; width: 100%;
    min-height: 46px; padding: 0 14px; border-radius: 14px; text-align: left; cursor: pointer;
    font-size: 14.5px; color: var(--agent-text-1);
    transition: background-color 140ms ease;
}
.opt.is-on { background: var(--agent-hover); }
@media (hover: hover) and (pointer: fine) {
    .opt:hover:not(:disabled) { background: var(--agent-hover); }
}
.opt:disabled { opacity: 0.6; cursor: default; }
.opt-text { flex: 1; min-width: 0; line-height: 1.35; }
.num {
    flex-shrink: 0; min-width: 20px; padding: 0 5px; border-radius: 6px; text-align: center;
    font-family: inherit; font-size: 11.5px; line-height: 20px; color: var(--agent-text-4);
    border: 1px solid var(--agent-card-border); opacity: 0; transition: opacity 140ms ease;
}
.opt:hover .num, .ask:focus-within .num { opacity: 1; }
@media (hover: none) { .num { display: none; } }

.tick {
    display: flex; align-items: center; justify-content: center; flex-shrink: 0;
    width: 20px; height: 20px; border-radius: 7px;
    border: 1.5px solid rgb(15 15 15 / 0.22); background: transparent;
    transition: background-color 160ms ease, border-color 160ms ease, transform 220ms var(--agent-spring, ease);
}
html.dark .tick { border-color: rgb(255 255 255 / 0.28); }
.tick svg { width: 13px; height: 13px; fill: none; stroke: #fff; stroke-width: 2; stroke-linecap: round; stroke-linejoin: round; stroke-dasharray: 16; stroke-dashoffset: 16; transition: stroke-dashoffset 260ms var(--agent-ease, ease); }
.ask.is-multi .opt .tick { border-radius: 999px; }
.ask.is-multi .opt.is-check .tick { border-radius: 6px; }
.opt.is-on .tick { background: var(--agent-accent); border-color: var(--agent-accent); transform: scale(1.04); }
.opt.is-on .tick svg { stroke-dashoffset: 0; }

.is-other { cursor: text; }
.is-text { background: transparent !important; }
.other-input {
    flex: 1; min-width: 0; height: 100%; padding: 0; border: 0; outline: none; box-shadow: none !important;
    background: transparent; font: inherit; color: var(--agent-text-1);
}
.other-input::placeholder { color: var(--agent-text-4); }

.foot {
    display: flex; align-items: center; gap: 8px; padding: 10px 8px 8px 16px;
    border-top: 1px solid var(--agent-card-border); margin: 4px -6px 0; padding-left: 22px; padding-right: 14px;
}
.count { flex: 1; font-size: 12.5px; color: var(--agent-text-3); }
.skip {
    height: 34px; padding: 0 16px; border-radius: 11px; font-size: 13.5px; color: var(--agent-text-1);
    background: var(--agent-card); border: 1px solid var(--agent-card-border);
    transition: background-color 140ms ease, transform 200ms var(--agent-spring, ease);
}
.skip:hover:not(:disabled) { background: var(--agent-hover); }
.skip:active { transform: scale(0.97); }
.go {
    display: flex; align-items: center; justify-content: center; width: 34px; height: 34px; border-radius: 11px;
    color: var(--agent-text-4); background: var(--agent-hover);
    transition: background-color 200ms ease, color 200ms ease, transform 220ms var(--agent-spring, ease);
}
.go svg { width: 16px; height: 16px; fill: none; stroke: currentColor; stroke-width: 1.8; stroke-linecap: round; stroke-linejoin: round; }
.go.is-ready { color: #fff; background: var(--agent-accent); }
.go.is-ready:hover { transform: translateX(1px) scale(1.04); }

.ask-step-enter-active, .ask-step-leave-active { transition: opacity 180ms ease, transform 220ms var(--agent-ease, ease); }
.ask-step-enter-from { opacity: 0; transform: translateX(14px); }
.ask-step-leave-to { opacity: 0; transform: translateX(-14px); }
@media (prefers-reduced-motion: reduce) { .ask-step-enter-active, .ask-step-leave-active { transition: none; } }

.keys {
    display: flex; flex-wrap: wrap; justify-content: center; gap: 4px 16px; margin-top: 10px;
    font-size: 11.5px; color: var(--agent-text-4);
}
.keys span + span::before { content: '·'; margin-right: 16px; }
@media (hover: none) { .keys { display: none; } }

/* ── Phones: quick replies ── */
.qr { min-width: 0; }
.qr-fold > div { padding: 2px 4px 8px; margin: -2px -4px -8px; }
.qr-list { display: flex; flex-direction: column; align-items: flex-end; gap: 8px; }
.qr-chip {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    max-width: 86%;
    min-height: 44px;
    padding: 10px 16px;
    border-radius: 22px;
    text-align: left;
    font-size: 15.5px;
    font-weight: 500;
    line-height: 1.32;
    overflow-wrap: anywhere;
    color: #d63d0e;
    background-color: rgba(255, 255, 255, 0.82);
    background-image: linear-gradient(180deg, rgba(255, 255, 255, 0.6), rgba(255, 255, 255, 0.1));
    box-shadow:
        inset 0 0 0 1px rgb(251 76 31 / 0.32),
        0 4px 14px rgba(36, 36, 36, 0.06),
        inset 1px 1px 1px -0.5px rgba(255, 255, 255, 1);
    -webkit-tap-highlight-color: transparent;
    -webkit-touch-callout: none;
    transition: opacity 200ms ease, transform 220ms var(--lh-spring-soft, ease), background-color 160ms ease;
}
html.dark .qr-chip {
    color: #ff9a70;
    background-color: rgba(68, 64, 60, 0.6);
    background-image: linear-gradient(180deg, rgba(255, 255, 255, 0.08), rgba(255, 255, 255, 0.02));
    box-shadow: inset 0 0 0 1px rgb(251 76 31 / 0.45), 0 4px 14px rgba(0, 0, 0, 0.3);
}
.qr-chip:disabled { opacity: 0.55; }
/* The tapped chip turns into the creator's bubble (and a copy of it flies to where that bubble lands). */
.qr-chip.is-picked {
    color: #fff;
    opacity: 1;
    background-color: transparent;
    background-image: linear-gradient(180deg, #ff6a3d 0%, #fb4c1f 100%);
    border-bottom-right-radius: 8px;
    box-shadow: 0 6px 16px rgba(251, 76, 31, 0.28), inset 1px 1px 1px -0.5px rgba(255, 255, 255, 0.7);
}
.qr-more { display: flex; align-items: center; justify-content: flex-end; gap: 4px; }
.qr-chip.is-other { color: var(--agent-text-2); box-shadow: 0 0 0 0.5px rgba(36, 36, 36, 0.1), 0 4px 14px rgba(36, 36, 36, 0.06); }
html.dark .qr-chip.is-other { color: var(--agent-text-2); box-shadow: 0 0 0 0.5px rgba(255, 255, 255, 0.14), 0 4px 14px rgba(0, 0, 0, 0.3); }
.qr-chip.is-other svg { width: 14px; height: 14px; fill: none; stroke: currentColor; stroke-width: 1.5; stroke-linejoin: round; }
.qr-skip {
    min-height: 44px;
    padding: 0 14px;
    border-radius: 22px;
    font-size: 15px;
    color: var(--agent-text-3);
    -webkit-tap-highlight-color: transparent;
}
.qr-skip:disabled { opacity: 0.5; }

/* ── Phones: several questions ── */
.qs { min-width: 0; }
.qs-card {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 12px;
    padding: 14px 14px 12px;
    border-radius: 22px;
    background-color: rgba(255, 255, 255, 0.82);
    background-image: linear-gradient(180deg, rgba(255, 255, 255, 0.6), rgba(255, 255, 255, 0.1));
    box-shadow:
        0 0 0 0.5px rgba(36, 36, 36, 0.08),
        0 6px 18px rgba(36, 36, 36, 0.07),
        inset 1px 1px 1px -0.5px rgba(255, 255, 255, 1);
    cursor: pointer;
    outline: none;
    -webkit-tap-highlight-color: transparent;
    -webkit-touch-callout: none;
    user-select: none;
}
html.dark .qs-card {
    background-color: rgba(68, 64, 60, 0.6);
    background-image: linear-gradient(180deg, rgba(255, 255, 255, 0.08), rgba(255, 255, 255, 0.02));
    box-shadow: 0 0 0 0.5px rgba(255, 255, 255, 0.12), 0 6px 18px rgba(0, 0, 0, 0.35);
}
.qs-card:focus-visible { box-shadow: 0 0 0 2px var(--agent-accent-ring); }
.qs-icon {
    display: grid;
    place-items: center;
    flex-shrink: 0;
    width: 40px;
    height: 40px;
    border-radius: 12px;
    background: rgb(251 76 31 / 0.1);
}
.qs-icon svg { width: 20px; height: 20px; fill: none; stroke: #e2400f; stroke-width: 1.6; stroke-linecap: round; stroke-linejoin: round; }
.qs-body { display: flex; flex: 1; flex-direction: column; min-width: 0; gap: 2px; }
.qs-title { font-size: 15.5px; font-weight: 600; color: var(--agent-text-1); }
.qs-sub { font-size: 13.5px; line-height: 1.35; color: var(--agent-text-3); display: -webkit-box; overflow: hidden; -webkit-line-clamp: 2; -webkit-box-orient: vertical; }
.qs-progress { display: flex; gap: 4px; margin-top: 6px; }
.qs-progress span { width: 18px; height: 4px; border-radius: 999px; background: var(--agent-press); }
.qs-progress span.is-done { background: var(--agent-accent); }
.qs-go svg { width: 14px; height: 14px; fill: none; stroke: currentColor; stroke-width: 2; stroke-linecap: round; stroke-linejoin: round; }
/* A full-width pill on its own row (like DraftCard's "Review & send"), so the text column keeps the card's width. */
.qs-go {
    display: flex;
    flex-basis: 100%;
    align-items: center;
    justify-content: center;
    gap: 6px;
    min-height: 44px;
    padding: 0 18px;
    border-radius: 14px;
    font-size: 15px;
    font-weight: 600;
    color: #fff;
    background: linear-gradient(180deg, var(--agent-accent-2) 0%, var(--agent-accent) 100%);
    box-shadow: inset 0 1px 0 rgb(255 255 255 / 0.35), 0 6px 16px -6px rgb(251 76 31 / 0.6);
}

/* On the voice call screen (always dark glass). */
.vc-card .qr-list { align-items: stretch; }
.vc-card .qr-chip {
    max-width: none;
    color: #fff;
    background: rgb(255 255 255 / 0.1);
    box-shadow: inset 0 0 0 1px rgb(255 255 255 / 0.16);
}
.vc-card .qr-more { justify-content: center; }
.vc-card .qr-skip { color: rgb(255 255 255 / 0.6); }
.vc-card .tick { border-color: rgb(255 255 255 / 0.28); }
.vc-card .ask:focus-within { border-color: rgb(255 255 255 / 0.18); box-shadow: 0 0 0 4px rgb(255 255 255 / 0.04); }
</style>
